import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '@/lib/api';

const AuthContext = createContext(null);

const API = `${getApiUrl()}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('linklite_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Silent push subscription auto-heal: re-register the device with the
      // backend on every authenticated app load so iOS/Android-pruned
      // subscriptions don't leave the user with no notifications. Runs
      // entirely in the background — no prompt unless permission was
      // already granted.
      try {
        const { autoHealPushSubscription } = await import('@/lib/push');
        autoHealPushSubscription();
      } catch (e) {
        // Non-fatal — push auto-heal failures should never block login.
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Re-run the push subscription auto-heal every time the app comes to the
  // foreground. iOS Safari + Android Chrome will quietly expire PWA push
  // subscriptions after inactivity, iOS updates, or storage cleanup — so a
  // single check on login isn't enough. This ensures the moment the app
  // becomes visible again we silently re-subscribe if permission is still
  // granted, so drivers/customers stop missing notifications between opens.
  useEffect(() => {
    if (!user) return;
    let inflight = false;
    const heal = async () => {
      if (inflight) return;
      inflight = true;
      try {
        const { autoHealPushSubscription } = await import('@/lib/push');
        await autoHealPushSubscription();
      } catch (e) {
        // silent — heal failures shouldn't affect the UI
      } finally {
        inflight = false;
      }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') heal();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', heal);
    // Also re-heal every 30 min while the tab stays open — some devices
    // (iPad, tablets left docked) never fire a visibility event but still
    // suffer subscription decay.
    const interval = setInterval(heal, 30 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', heal);
      clearInterval(interval);
    };
  }, [user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('linklite_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const registerCustomer = async (data) => {
    const response = await axios.post(`${API}/auth/register/customer`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('linklite_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const registerDriver = async (data) => {
    const response = await axios.post(`${API}/auth/register/driver`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('linklite_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('linklite_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUser = async (data) => {
    if (!user) return;
    const response = await axios.put(`${API}/auth/user/${user.id}`, data);
    setUser(response.data);
    return response.data;
  };

  // Lightweight local user-state patch (no API call) — useful after side-effect
  // uploads like /me/avatar where the server already saved the change.
  const patchUser = (patch) => setUser((u) => (u ? { ...u, ...patch } : u));

  const refreshUser = () => fetchUser();

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      registerCustomer,
      registerDriver,
      updateUser,
      patchUser,
      refreshUser,
      isAuthenticated: !!user,
      isDriver: user?.role === 'driver',
      isCustomer: user?.role === 'customer',
      isAdmin: user?.role === 'admin' || user?.is_admin === true
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
